import { Button, Drawer, Icons } from "@components/index";
import {
  useGetJustificationHistory,
} from "@services/justification/hooks";
import styles from "./JustificationHistory.module.scss";

interface JustificationHistoryProps {
  row: Sentinel.DelayManagement.Item;
  processDefinitionId: number;
  isOpen: boolean;
  onClose: () => void;
}

const statusLabels: Record<string, string> = {
  AwaitingJustification: "Aguardando justificativa",
  WaitingJustification: "Aguardando justificativa",
  OnHold: "Em espera",
  Doing: "Em andamento",
  Concluded: "Concluído",
};

const sourceFeatureLabels: Record<string, string> = {
  DelayManagement: "Gestão de Atraso",
  Timeline: "Timeline",
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const getStatusLabel = (status: string) =>
  statusLabels[status] ?? statusLabels[status[0]?.toUpperCase() + status.slice(1)] ?? status;

const getSourceFeatureLabel = (sourceFeature: string) =>
  sourceFeatureLabels[sourceFeature] ?? sourceFeature;

const HistoryEvent: React.FC<{ item: Sentinel.DelayManagement.JustificationHistoryItem }> = ({ item }) => (
  <li className={styles.event}>
    <span className={styles.marker} aria-hidden="true">
      <Icons icon="check" size="sm" />
    </span>
    <div className={styles.eventContent}>
      <strong>{getStatusLabel(item.actionStatus)}</strong>
      <span>Origem da alteração: {item.sourceFeature ? getSourceFeatureLabel(item.sourceFeature) : "-"}</span>
      <span>Horário da alteração: {formatDateTime(item.creationDate)}</span>
      <span>Incluído por: {item.user || "-"}</span>
    </div>
  </li>
);

const JustificationHistory: React.FC<JustificationHistoryProps> = ({
  row,
  processDefinitionId,
  isOpen,
  onClose,
}) => {
  const { data: history = [], isLoading, isError } = useGetJustificationHistory(
    row.dateOfDelay,
    processDefinitionId,
    isOpen
  );

  return (
    <Drawer
      overlay
      title="Acompanhar histórico"
      description="Acompanhe o histórico de tratativa e justificativa dos processos da timeline."
      isOpen={isOpen}
      onClose={onClose}
      idUsedToOpenDrawer="open-drawer-justification-history"
      customFooter={() => (
        <div className={styles.footer}>
          <Button label="Fechar" buttonStyle="primary" size="md" onClick={onClose} />
        </div>
      )}
    >
      <div className={styles.content}>
        <div className={styles.process}>{row.processName}</div>
        {isLoading && <p className={styles.feedback}>Carregando histórico...</p>}
        {isError && <p className={styles.feedback}>Não foi possível carregar o histórico.</p>}
        {!isLoading && !isError && history.length === 0 && (
          <p className={styles.feedback}>Nenhum histórico encontrado.</p>
        )}
        {!isLoading && !isError && history.length > 0 && (
          <ol className={styles.timeline}>
            {history.map((item) => <HistoryEvent item={item} key={item.id} />)}
          </ol>
        )}
      </div>
    </Drawer>
  );
};

export default JustificationHistory;
